import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * ModernAppShell — the top-level layout module: icon rail, optional secondary
 * nav column, top command bar, scrolling workspace, and an optional persistent
 * assistant region. It applies the `oneup-modern` scope class so every modern
 * token resolves inside the subtree (and ONLY inside it).
 *
 * It owns layout only — nav data, search behavior, and assistant wiring are
 * passed in as already-built nodes/adapters from the app, so legacy and modern
 * share the exact same underlying actions.
 *
 * Height: fills its parent (`h-full`). Use `className="h-screen"` for a real
 * full-app mount, or wrap in a fixed-height box for an embedded preview.
 */
export function ModernAppShell({
  rail,
  secondaryNav,
  topBar,
  assistant,
  children,
  className,
}: {
  /** ModernLeftRail (or any node) for the primary icon rail. */
  rail?: ReactNode;
  /** Optional expanded nav column between the rail and the workspace. */
  secondaryNav?: ReactNode;
  /** ModernTopBar (or any node). */
  topBar?: ReactNode;
  /** Optional persistent right-hand assistant panel (e.g. AliceDrawer docked). */
  assistant?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'oneup-modern flex h-full w-full overflow-hidden bg-background text-foreground',
        className,
      )}
    >
      {rail}
      {secondaryNav && (
        <div className="hidden w-[var(--mui-sidebar-width)] flex-shrink-0 border-r border-border bg-card lg:block">
          {secondaryNav}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {topBar}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-6">{children}</div>
        </main>
      </div>
      {assistant && (
        <aside className="hidden w-[var(--mui-assistant-width)] flex-shrink-0 border-l border-border bg-card xl:block">
          {assistant}
        </aside>
      )}
    </div>
  );
}

/**
 * ModernSecondaryNav — optional expanded nav list for the column next to the
 * rail. Grouped links, quiet styling, single accent on the active item.
 */
export interface ModernNavLink {
  id: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  badge?: ReactNode;
}

export interface ModernNavGroup {
  id: string;
  label?: string;
  items: ModernNavLink[];
}

export function ModernSecondaryNav({
  header,
  groups,
  activeId,
  onSelect,
  footer,
}: {
  header?: ReactNode;
  groups: ModernNavGroup[];
  activeId?: string;
  onSelect?: (id: string) => void;
  footer?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      {header && <div className="border-b border-border px-4 py-4">{header}</div>}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {groups.map((group) => (
          <div key={group.id} className="mb-4 last:mb-0">
            {group.label && (
              <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {group.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect?.(item.id)}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-[calc(var(--radius)-6px)] px-2.5 py-2 text-[13px] transition-colors',
                        active
                          ? 'bg-primary/[0.08] font-medium text-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      {item.badge}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
    </div>
  );
}
